import { FC, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { CreateFormTheme } from "../../utils/themes";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import { capitalize } from "lodash";
import { useTheme } from "../../middleware/ThemeContext";
import StyledCheckBox from "../StyledComponents/CheckBox/StyledCheckBox";
import SimpleBar from "simplebar-react";

const CustomDropdown = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div<{ error?: boolean }>(({ theme, error }) => ({
  padding: '8px 8px',
  height: '42px',
  border: `1px solid ${error
    ? theme.CreateFormTheme?.colors?.error
    : theme.CreateFormTheme?.colors?.inputBorders}`,
  borderRadius: CreateFormTheme?.borderRadius,
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: theme.CreateFormTheme?.colors?.inputBackground,
  color: theme.CreateFormTheme?.colors?.placeHolder,

  '&:focus': {
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px',
    outline: `2px solid ${theme.CreateFormTheme?.colors?.border}`,
  },
}));

const DropdownList = styled.div<{ $isOpen: boolean; $direction: 'up' | 'down' }>(({ theme, $isOpen, $direction }) => ({
  position: 'absolute',
  width: '100%',
  maxHeight: '200px',
  // overflowY: 'auto',
  border: `1px solid ${theme.CreateFormTheme.colors.border}`,
  borderRadius: $direction === 'down' ? "0 0 5px 5px" : "5px 5px 0 0",
  background: theme.CreateFormTheme.colors.inputBackground,
  zIndex: 100,
  display: $isOpen ? 'block' : 'none',
  top: $direction === 'down' ? '100%' : 'auto',
  bottom: $direction === 'up' ? '100%' : 'auto',
  marginTop: $direction === 'down' ? '4px' : '0',
  marginBottom: $direction === 'up' ? '4px' : '0',
  // boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  boxShadow: "-4px 4px 40px 0px #0000001A",
  borderImage: theme.CorporateTheme.linearGradient,
  borderImageSlice: 1,
}));

const CheckboxLabel = styled.label(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: CreateFormTheme.spacing.sm,
  fontFamily: '"Raleway", sans-serif',
  padding: '12px 18px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px solid #eee',
  transition: 'background-color 0.2s ease',
  color: theme.CorporateTheme.colors.textSecondary,

  '&:hover': {
    backgroundColor: theme.CorporateTheme.colors.headerBG,
  },

  '&:last-child': {
    borderBottom: 'none',
  },
}));

// interface MultiSelectDropdownProps<T extends string | number = string | number> {
//   options: { id: number; name: string; value: T }[];
//   selected: T[];
//   onSelect: (value: T) => void;
//   placeholder?: string;
//   style?: React.CSSProperties;
//   buttonStyle?: React.CSSProperties;
//   error?: boolean;
//   count?: any
//   disabled?: boolean
// }

// const MultiSelectDropdown = <T extends string | number = string | number>({
//   style,
//   buttonStyle,
//   options,
//   selected,
//   onSelect,
//   placeholder,
//   error,
//   count,
//   disabled
// }: MultiSelectDropdownProps<T>) => {
//   const { theme } = useTheme();
//   const [isOpen, setIsOpen] = useState(false);
//   const dropdownRef = useRef<HTMLDivElement>(null);

//   const handleClickOutside = (event: MouseEvent) => {
//     if (
//       dropdownRef.current &&
//       !dropdownRef.current.contains(event.target as Node)
//     ) {
//       setIsOpen(false);
//     }
//   };

//   useEffect(() => {
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   return (
//     <CustomDropdown style={style} ref={dropdownRef} aria-disabled={disabled}>
//       <DropdownHeader
//         theme={theme}
//         error={error}
//         onClick={() => setIsOpen(!isOpen)}
//         style={buttonStyle}
//       >
//         {selected.length > 0
//           ? count
//             ? selected?.length + " selected"
//             : selected.map((item) => capitalize(String(item))).join(", ")
//           : placeholder}
//         <ChevronDownIcon />
//       </DropdownHeader>

//       <DropdownList theme={theme} $isOpen={isOpen}>
//         {options?.map((option) => (
//           <CheckboxLabel theme={theme} key={option.value}>
//             <input
//               type="checkbox"
//               checked={selected.includes(option.value)}
//               onChange={() => onSelect(option.value)}
//               value={option.value}
//             />
//             {option.name}
//           </CheckboxLabel>
//         ))}
//       </DropdownList>
//     </CustomDropdown>
//   );
// };

// export default MultiSelectDropdown;

interface MultiSelectDropdownProps<T extends string | number = string | number> {
  options: { id: number; name: string; value: T }[];
  selected: T[];
  onSelect: (value: T) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  error?: boolean;
  count?: boolean;
  disabled?: boolean;

  // Async / Pagination props
  isLoading?: boolean;
  hasMore?: boolean;
  onFetchMore?: () => void; // called when scroll reaches bottom
  className?: string
}

const MultiSelectDropdown = <T extends string | number = string | number>({
  style,
  buttonStyle,
  options,
  selected,
  onSelect,
  placeholder,
  error,
  count,
  disabled,
  isLoading,
  hasMore,
  onFetchMore,
  className
}: MultiSelectDropdownProps<T>) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState<'up' | 'down'>('down');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // infinite scroll handler
  const handleScroll = () => {
    if (!listRef.current || !onFetchMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      onFetchMore();
    }
  };

  return (
    <CustomDropdown style={style} ref={dropdownRef} aria-disabled={disabled} className={className}>
      <DropdownHeader
        theme={theme}
        error={error}
        onClick={() => {
          if (disabled) return;

          if (!isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const requiredSpace = 300; // maxHeight + margin
            if (spaceBelow < requiredSpace && spaceAbove > spaceBelow) {
              setOpenDirection('up');
            } else {
              setOpenDirection('down');
            }
          }

          setIsOpen((prev) => !prev);
        }}
        style={buttonStyle}
      >
        {selected.length > 0
          ? count
            ? `${selected.length} selected`
            : selected.map((item) => capitalize(String(item))).join(", ")
          : placeholder}
        <ChevronDownIcon color={theme.CorporateTheme.colors.textSecondary} />
      </DropdownHeader>

      <DropdownList
        theme={theme}
        $isOpen={isOpen}
        $direction={openDirection}
        // ref={listRef}
        // onScroll={handleScroll}
        // style={{ maxHeight: "200px", overflowY: "auto" }}
      >
        <SimpleBar autoHide={false} style={{ maxHeight: 200 }} scrollableNodeProps={{ ref: listRef, onScroll: handleScroll }}
        >
          {options?.map((option) => (
            <CheckboxLabel theme={theme} key={option.value}>
              {/* <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => onSelect(option.value)}
              value={option.value}
            /> */}
              <StyledCheckBox
                className="cursor-pointer d-flex align-items-center justify-content-center rounded-1"
                size={16}
                borderColor={
                  selected.includes(option.value)
                    ? theme.CorporateTheme?.colors?.primary
                    : "#626262"
                }
                bgColor={theme.CorporateTheme?.colors?.primary}
                checkColor={theme.colors.contentBackground}
                value={selected.includes(option.value)}
                onChange={() => onSelect(option.value)}
                label={option.name}
              />
              {/* {option.name} */}
            </CheckboxLabel>
          ))}

          {isLoading && <div className="p-2 text-muted text-center">Loading...</div>}
          {!isLoading && hasMore && (
            <div className="p-2 text-muted text-center">Scroll to load more...</div>
          )}
        </SimpleBar>
      </DropdownList>
    </CustomDropdown>
  );
};


export default MultiSelectDropdown;
